import React from 'react';

import { RedocStandalone } from 'redoc';
import { Title } from 'react-admin';
import semverGte from 'semver/functions/gte';
import repairApiSpec from '@troverecommerce/ps-repair-api-core/build/ps-repair-api.json';
import returnApiSpec from '@troverecommerce/ps-return-api-core/build/ps-return-api.json';
import tradeinApiSpec from '@troverecommerce/ps-tradein-api-core/build/ps-tradein-api.json';
import uploadApiSpec from '@troverecommerce/ps-upload-api-core/build/ps-upload-api.json';
import webhooksReWMS from '@src/docs/webhooks-rewms.md';
import rmsIntegrationGuide from '@src/docs/rms-integration-guide.md';
import { getEnv } from '@src/apis/shared';
import { OpenAPIV3_1 } from 'openapi-types';
import { useTitle } from '@src/hooks/useTitle';


const YERDLE_ENV = getEnv('YERDLE_ENV');

const getPaths = (specs: OpenAPIV3_1.PathsObject<{}, {}> | undefined, prefix: string) => {
  if (!specs) return {};
  return Object.keys(specs).reduce((memo, path) => {
    return {
      ...memo,
      [`/${prefix}${path}`]: specs[path],
    };
  }, {});
};

const getServerUrl = () => {
  if (['development', 'qa', 'staging', 'sandbox'].includes(YERDLE_ENV)) {
    return `https://api.${YERDLE_ENV}.trove.com/v2`;
  }
  return 'https://api.trove.com/v2';
};

const servers = [
  {
    url: getServerUrl(),
    description: 'Production Partner Suite API',
  }
];

const info = {
  title: 'Trove Partner Suite API',
  description: `## Overview

The Trove API is organized around [REST](http://en.wikipedia.org/wiki/Representational_State_Transfer). All APIs are based on [JSON:API v1.1](https://jsonapi.org/format/). JSON:API requires use of the JSON:API media type ([application/vnd.api+json](https://www.iana.org/assignments/media-types/application/vnd.api+json)) for exchanging data. This means all requests are expected to have a \`content-type\` header containing \`application/vnd.api+json\` and all responses will use a \`content-type\` header containing \`application/vnd.api+json\`.

## Base URLs

- Sandbox - https://api.sandbox.trove.com/v2
- Production - https://api.trove.com/v2

Use the sandbox environment for testing purposes.

## Authentication

The Trove API uses API keys to authenticate requests. Trove will provide API keys during the initial integration process. All requests expect the \`x-api-key\` header to contain a valid API key.

Your API keys carry many privileges, so be sure to keep them secure! Do not share your secret API keys in publicly accessible areas such as GitHub, client-side code, and so forth.

All API requests must be made over HTTPS. Calls made over plain HTTP will fail. API requests without authentication will also fail.

## Rate Limits

There are no global rate limits at this time. Individual APIs may specify their own limits (for example, the Upload API allows 1 concurrent upload per API key). Refer to each API's documentation for specific restrictions.

## Authorization

Each API has an _Authorizations_ section that documents the required scopes needed to call that API. If you are receiving a 403 response, ensure that your API key has the proper scopes assigned to it.`,
  termsOfService: 'https://trove.com/terms-of-service/',
  version: '2.0.0',
  contact: {
    name: 'Trove Engineering',
    email: 'dev@trove.co'
  }
};

const getSpec = (spec: OpenAPIV3_1.Document): OpenAPIV3_1.Document => {
  if (YERDLE_ENV === 'production' && !semverGte(spec.info.version, '1.0.0')) {
    return {
      openapi: '3.1.1',
      info: {
        summary: 'Not available',
        title: 'Not available',
        version: spec.info.version,
      },
      paths: undefined,
      components: {},
    };
  }
  return spec;
};

const tradeinSpec = getSpec(tradeinApiSpec as OpenAPIV3_1.Document);
const repairSpec = getSpec(repairApiSpec as OpenAPIV3_1.Document);
const returnSpec = getSpec(returnApiSpec as OpenAPIV3_1.Document);
const uploadSpec = getSpec(uploadApiSpec as OpenAPIV3_1.Document);

const mergedSpec = {
  openapi: '3.1.1',
  servers,
  info,
  paths: {
    ...getPaths(tradeinSpec.paths, 'tradein'),
    ...getPaths(repairSpec.paths, 'repair'),
    ...getPaths(returnSpec.paths, 'return'),
    ...getPaths(uploadSpec.paths, 'upload'),
  },
  components: {
    ...tradeinSpec.components,
    ...repairSpec.components,
    ...returnSpec.components,
    ...uploadSpec.components,
    schemas: {
      ...tradeinSpec.components?.schemas,
      ...repairSpec.components?.schemas,
      ...returnSpec.components?.schemas,
      ...uploadSpec.components?.schemas,
    },
    responses: {
      ...tradeinSpec.components?.responses,
      ...repairSpec.components?.responses,
      ...returnSpec.components?.responses,
      ...uploadSpec.components?.responses,
    },
    securitySchemes: {
      ...tradeinSpec.components?.securitySchemes,
      ...repairSpec.components?.securitySchemes,
      ...returnSpec.components?.securitySchemes,
      ...uploadSpec.components?.securitySchemes,
    },
  },
  tags: [
    ...(tradeinSpec.tags || []),
    ...(repairSpec.tags || []),
    ...(returnSpec.tags || []),
    ...(uploadSpec.tags || []),
    {
      name: 'ReWMS',
      description: webhooksReWMS,
    },
    {
      name: 'RMS Integration',
      description: rmsIntegrationGuide,
    },
  ],
  'x-tagGroups': [
    {
      name: 'Guides',
      tags: ['RMS Integration'],
    },
    {
      name: 'API Endpoints',
      tags: ['tradeIn', 'repair', 'return', 'upload'], // your existing API tags
    },
    {
      name: 'Webhooks',
      tags: ['ReWMS'],
    },
  ],
};


const ApiDocView = () => {
  const { title } = useTitle('API Documentation');

  return (
    <>
      <Title title={title} />
      <RedocStandalone
        spec={mergedSpec}
      />
    </>
  );
};

export default ApiDocView;
